import { DEFAULT_THEME, PALETTE, getColor } from "@zendeskgarden/react-theming"
import { breakpoints } from "../lib/media-queries"

// The default constant below allows us to overwrite styles for Garden components
// in dark mode without having to also manually reset the value in light mode.
// The value of the constant is arbitrary, but by setting it as an intentionally
// invalid CSS property value, we can trust the browser to automatically fall
// through to the value initially set for that property on that component.
// This enforces explicit definitions in the individual theme objects for Garden
// component style overrides.
const GARDEN_DEFAULT = "Garden Default"

const commonOverrides = {
  breakpoints,
  focusBoxShadow: `${DEFAULT_THEME.shadows.md(
    getColor("primaryHue", 600, DEFAULT_THEME, 0.35)
  )} inset`, // from Garden source for focus style on other elements
  layout: {
    header: {
      height: "60px"
    },
    search: {
      input: {
        width: "340px"
      }
    },
    leftColumn: {
      width: "360px"
    },
    rightColumn: {
      width: "280px"
    },
    document: {
      maxWidth: "700px"
    },
    index: {
      maxWidth: "1160px"
    }
  },
  fontSizes: {
    ...DEFAULT_THEME.fontSizes,
    // xs: "10px",
    // sm: "12px",
    // md: "14px",
    // lg: "18px",
    // xl: "22px",
    // xxl: "26px",
    // xxxl: "36px",
    x4l: "48px",
    x5l: "60px",
    document: {
      subheading: "16px"
    }
  },
  lineHeights: {
    ...DEFAULT_THEME.lineHeights,
    // sm: "16px",
    // md: "20px",
    // lg: "24px",
    // xl: "28px",
    // xxl: "32px",
    // xxxl: "44px",
    x4l: "52px",
    x5l: "60px",
    document: {
      subheading: "24px"
    }
  },
  letterSpacing: {
    subheading: "0.13px"
  },
  space: {
    ...DEFAULT_THEME.space,
    // base: 4,
    // xxs: "4px",
    // xs: "8px",
    // sm: "12px",
    // md: "20px",
    // lg: "32px",
    // xl: "40px",
    // xxl: "48px",
    xxxl: "64px",
    horz: {
      sm: "16px",
      md: "24px",
      lg: "32px",
      xl: "48px"
    }
  }
}

export const lightTheme = {
  ...DEFAULT_THEME,
  ...commonOverrides,
  colors: {
    ...DEFAULT_THEME.colors,
    foreground: PALETTE.grey[800],
    background: PALETTE.white,
    changeTag: {
      added: {
        color: PALETTE.white,
        background: PALETTE.green[600]
      },
      breakingChange: {
        color: PALETTE.white,
        background: PALETTE.crimson[600]
      },
      deprecated: {
        color: PALETTE.white,
        background: PALETTE.crimson[600]
      },
      eapEnded: {
        color: PALETTE.yellow[800],
        background: PALETTE.yellow[400]
      },
      fixed: {
        color: PALETTE.white,
        background: PALETTE.blue[600]
      },
      newEap: {
        color: PALETTE.yellow[800],
        background: PALETTE.yellow[400]
      },
      removed: {
        color: PALETTE.white,
        background: PALETTE.crimson[600]
      }
    },
    document: {
      headings: PALETTE.kale[700],
      headingsSecondary: PALETTE.kale[600],
      subheading: PALETTE.grey[600],
      tocLinkBorder: PALETTE.grey[300],
      tocLinkBorderHover: PALETTE.blue[600],
      links: PALETTE.blue[600],
      codeBackground: GARDEN_DEFAULT,
      codeBlockBackground: GARDEN_DEFAULT,
      tables: {
        header: GARDEN_DEFAULT,
        borders: GARDEN_DEFAULT,
        rowHover: GARDEN_DEFAULT
      },
      tagBackground: GARDEN_DEFAULT,
      horizontalRule: PALETTE.grey[300]
    },
    sidebar: {
      background: "rgba(246, 244, 244, 1.0)", // not in default palette
      foreground: PALETTE.grey[800],
      subheading: PALETTE.kale[600],
      linkBackgroundHover: getColor("grey", 800, DEFAULT_THEME, 0.05),
      linkBackgroundActive: getColor("grey", 800, DEFAULT_THEME, 0.1),
      accordionTopBorder: PALETTE.grey[200],
      breadcrumbs: PALETTE.grey[800]
    },
    footer: {
      border: PALETTE.grey[200],
      heading: PALETTE.kale[600],
      companyName: PALETTE.grey[800],
      address: PALETTE.grey[600],
      link: PALETTE.blue[600]
    },
    header: {
      textColor: PALETTE.grey[800],
      background: PALETTE.white,
      logoColor: PALETTE.kale[600],
      mobileNavToggle: PALETTE.grey[600],
      mobileSearchToggle: PALETTE.grey[600],
      mobileBorder: PALETTE.grey[200],
      mobileNavLinkActive: PALETTE.grey[200],
      mobileNavLinkHover: getColor("grey", 200, DEFAULT_THEME, 0.5),
      desktopNavLinkActive: PALETTE.blue[600],
      desktopNavLinkHover: PALETTE.blue[500],
      mobileBackdrop: getColor("grey", 800, DEFAULT_THEME, 0.5),
      shadow: getColor("grey", 800, DEFAULT_THEME, 0.05)
    },
    search: {
      slashIconBackground: PALETTE.grey[100],
      slashIconBorder: PALETTE.grey[200],
      searchBarIcon: PALETTE.grey[600],
      buttonBorder: PALETTE.grey[300],
      buttonBorderHover: PALETTE.blue[600],
      buttonHover: getColor("blue", 100, DEFAULT_THEME, 0.5),
      buttonText: DEFAULT_THEME.colors.foreground,
      inputBorder: GARDEN_DEFAULT,
      searchHeader: PALETTE.kale[600],
      searchDescription: PALETTE.grey[600],
      resultHover: PALETTE.blue[100],
      hintText: PALETTE.grey[800],
      closeButtonColor: GARDEN_DEFAULT,
      closeButtonHover: GARDEN_DEFAULT,
      loader: GARDEN_DEFAULT
    },
    tag: {
      background: "#554f9e"
    },
    metadata: {
      background: "#F3F0EE",
      separatorBorder: PALETTE.grey[300]
    },
    aside: {
      noticeBackgroundColor: PALETTE.blue[200],
      warningBackgroundColor: PALETTE.yellow[200],
      infoBackgroundColor: PALETTE.grey[200],
      infoInlineCodeBackgroundColor: PALETTE.grey[300]
    }
  }
}

export const darkTheme = {
  ...DEFAULT_THEME,
  ...commonOverrides,
  colors: {
    ...DEFAULT_THEME.colors,
    foreground: PALETTE.kale[300],
    background: getColor("kale", 850, DEFAULT_THEME, 1),
    changeTag: {
      added: {
        color: PALETTE.green[800],
        background: PALETTE.green[300]
      },
      breakingChange: {
        color: PALETTE.red[800],
        background: PALETTE.red[300]
      },
      deprecated: {
        color: PALETTE.red[800],
        background: PALETTE.red[300]
      },
      eapEnded: {
        color: PALETTE.yellow[800],
        background: PALETTE.yellow[300]
      },
      fixed: {
        color: PALETTE.blue[800],
        background: PALETTE.blue[300]
      },
      newEap: {
        color: PALETTE.yellow[800],
        background: PALETTE.yellow[300]
      },
      removed: {
        color: PALETTE.red[800],
        background: PALETTE.red[300]
      }
    },
    document: {
      headings: PALETTE.kale[200],
      headingsSecondary: PALETTE.kale[200],
      subheading: PALETTE.kale[400],
      tocLinkBorder: PALETTE.kale[600],
      tocLinkBorderHover: PALETTE.kale[300],
      links: PALETTE.blue[400],
      codeBackground: PALETTE.kale[600],
      codeBlockBackground: getColor("kale", 950, DEFAULT_THEME, 1),
      tables: {
        header: PALETTE.kale[200],
        borders: PALETTE.kale[600],
        rowHover: "rgba(255, 255, 255, 0.08)"
      },
      tagBackground: PALETTE.kale[600],
      horizontalRule: PALETTE.kale[600]
    },
    sidebar: {
      background: PALETTE.kale[700],
      foreground: PALETTE.kale[200],
      subheading: PALETTE.kale[400],
      linkBackgroundHover: "rgba(255, 255, 255, 0.05)",
      linkBackgroundActive: "rgba(255, 255, 255, 0.1)",
      accordionTopBorder: PALETTE.kale[600],
      breadcrumbs: PALETTE.kale[200]
    },
    footer: {
      border: PALETTE.kale[600],
      heading: PALETTE.kale[400],
      companyName: PALETTE.kale[200],
      address: PALETTE.kale[400],
      link: PALETTE.blue[400]
    },
    header: {
      textColor: PALETTE.kale[100],
      background: PALETTE.kale[700],
      logoColor: PALETTE.white,
      mobileNavToggle: PALETTE.white,
      mobileSearchToggle: PALETTE.kale[100],
      mobileBorder: PALETTE.kale[600],
      mobileNavLinkActive: PALETTE.kale[600],
      mobileNavLinkHover: getColor("kale", 600, DEFAULT_THEME, 0.7),
      desktopNavLinkActive: PALETTE.kale[100],
      desktopNavLinkHover: PALETTE.kale[200],
      mobileBackdrop: "rgba(1, 24, 27, 0.75)", // not in default palette
      shadow: "rgba(1, 24, 27, 0.15)" // not in default palette
    },
    search: {
      slashIconBackground: PALETTE.kale[600],
      slashIconBorder: PALETTE.kale[600],
      searchBarIcon: PALETTE.white,
      buttonBorder: PALETTE.kale[500],
      buttonBorderHover: PALETTE.kale[400],
      buttonHover: "rgba(255, 255, 255, 0.05)",
      buttonText: PALETTE.kale[300],
      inputBorder: PALETTE.kale[500],
      searchHeader: PALETTE.kale[400],
      searchDescription: PALETTE.kale[400],
      resultHover: PALETTE.kale[700],
      hintText: PALETTE.kale[200],
      closeButtonColor: PALETTE.kale[200],
      closeButtonHover: getColor("kale", 600, DEFAULT_THEME, 0.7),
      loader: "rgba(250, 250, 250, 0.1)" // not in default palette
    },
    tag: {
      background: "#554f9e"
    },
    aside: {
      noticeBackgroundColor: GARDEN_DEFAULT,
      warningBackgroundColor: GARDEN_DEFAULT,
      infoBackgroundColor: GARDEN_DEFAULT,
      infoInlineCodeBackgroundColor: GARDEN_DEFAULT
    }
  }
}
